import { useEffect, useRef, useState } from 'react';
import { RECORDING_NOTICE_STATUSES, type RecordingStatus } from '../../api/callRecording';

/** Whether the pre-connect consent notice applies to this recording status. */
export function showsRecordingNotice(status: RecordingStatus | null | undefined): boolean {
  return !!status && RECORDING_NOTICE_STATUSES.has(status);
}

/**
 * Shown on ringing / connecting screens of a call that will be recorded.
 * Staying on (or answering) the call is the consent; the copy says so.
 * `manual`: nothing records unless someone on the call presses Start
 * recording, so it says the call *may* be recorded and how they will know.
 */
export function RecordingNotice({ mode = 'auto' }: { mode?: 'auto' | 'manual' }) {
  return (
    <div className="cs-rec-notice" role="note">
      <span className="cs-rec-notice__title">
        <span className="cs-rec__dot" aria-hidden />
        {mode === 'manual' ? 'This call may be recorded' : 'This call will be recorded'}
      </span>
      <span className="cs-rec-notice__body">
        {mode === 'manual'
          ? "Anyone on the call set to record can start recording. You'll see REC if they do."
          : 'Answering means you agree. You can decline or leave at any time.'}
      </span>
    </div>
  );
}

/** Start recording on a connected "During call" call; there is no stop (it
 *  ends with the call). Everyone sees the REC badge once it is live. */
export function StartRecordingButton({ onStart, busy = false }: { onStart: () => void; busy?: boolean }) {
  return (
    <button
      type="button"
      className="cs-rec-start"
      onClick={onStart}
      disabled={busy}
      aria-busy={busy}
    >
      <span className="cs-rec__dot" aria-hidden />
      {busy ? 'Starting…' : 'Start recording'}
    </button>
  );
}

/**
 * Which recording UI a call screen shows. A pending `manual` recording is a
 * "may be recorded" notice before connecting; once connected the notice goes
 * and whoever may start it gets the Start recording button. Anything else
 * keeps the "will be recorded" notice until the REC badge takes over.
 */
export function recordingUi(
  status: RecordingStatus | null,
  startMode: 'auto' | 'manual' | null | undefined,
  connected: boolean,
  canStart: boolean,
): { mode: 'auto' | 'manual'; hideNotice: boolean; showStart: boolean } {
  const manualPending = startMode === 'manual' && status === 'pending';
  return {
    mode: manualPending ? 'manual' : 'auto',
    hideNotice: manualPending && connected,
    showStart: manualPending && connected && canStart,
  };
}

/** Red pulsing REC badge while the server is recording this call. */
export function RecBadge({ compact = false }: { compact?: boolean }) {
  return (
    <span
      className={`cs-rec${compact ? ' cs-rec--compact' : ''}`}
      role="status"
      aria-label="This call is being recorded"
      title="This call is being recorded"
    >
      <span className="cs-rec__dot" aria-hidden />
      REC
    </span>
  );
}

/** Busy state around a screen's Start recording handler (one press at a time). */
export function useStartRecording(onStartRecording?: () => void | Promise<unknown>) {
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const start = () => {
    if (busy || !onStartRecording) return;
    setBusy(true);
    let pending: unknown;
    try { pending = onStartRecording(); } catch { /* the handler reports its own errors */ }
    Promise.resolve(pending)
      .catch(() => { /* the handler reports its own errors */ })
      .finally(() => { if (mounted.current) setBusy(false); });
  };
  return { busy, start };
}
